/**
 * Client-side slide parser for presentation artifacts.
 *
 * Mirrors `packages/ai/src/artifacts/handlers/presentation/slides.ts` so the canvas
 * can split the artifact body into per-slide HTML without importing the
 * server-only `@taqat/ai` package. Same regex, same id semantics — keep
 * the two files in sync if either changes.
 *
 * Depth-aware, not a naive lazy match: nothing forbids a model from nesting
 * a `<section>` inside a slide for internal layout, and a lazy
 * `<section>...?<\/section>` regex would close on that INNER tag, truncating
 * the slide and shifting every match after it.
 *
 * @module lib/artifacts/slides
 */

/** One slide carved out of the artifact body. */
export interface ParsedSlide {
  /** Stable identifier from `data-slide-id` (or generated when missing). */
  id: string;
  /** Original outer HTML of the `<section>` element. */
  html: string;
}

/** Matches a `<section ...>` open tag or a `</section>` close tag. */
const SECTION_TAG_RE = /<(\/?)section\b[^>]*>/gi;
const ID_RE = /\bdata-slide-id="([^"]+)"/i;

/**
 * Scans `text` for top-level `<section>...</section>` blocks, tracking
 * nesting depth so an inner `<section>` doesn't prematurely close the outer
 * match.
 */
function matchTopLevelSections(text: string): string[] {
  const re = new RegExp(SECTION_TAG_RE.source, SECTION_TAG_RE.flags);
  const complete: string[] = [];
  let depth = 0;
  let start = -1;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    const isClose = match[1] === "/";
    if (!isClose) {
      if (depth === 0) start = match.index;
      depth++;
      continue;
    }
    if (depth === 0) continue; // stray close tag with no matching open
    depth--;
    if (depth === 0 && start !== -1) {
      complete.push(text.slice(start, match.index + match[0].length));
      start = -1;
    }
  }
  return complete;
}

/** Splits an artifact body into discrete slides. */
export function parseSlides(content: string): ParsedSlide[] {
  if (!content) return [];
  const matches = matchTopLevelSections(content);
  return matches.map((html, idx) => {
    const idMatch = html.match(ID_RE);
    return {
      id: idMatch?.[1] ?? `s${idx + 1}`,
      html,
    };
  });
}
